/** 折法几何：纸张、可编辑区域、展开变换、剪口展开。
 *
 * 核心结论（见 docs/fold-geometry.md）：
 *   展开是**连续反射**，不是旋转。相邻层手性交替。
 *   U_0 = S, U_{k+1} = Reflect(baseAngle + (k+1)·α)(U_k)
 *
 * 该实现与折法一一对应，不做"按层数复制扇区"的近似。
 */
import type { CutShape, FinishedSize, FoldId, Polygon, Vec } from '../types';
import { getFold, type FoldDefinition } from './definitions';
import { apply, reflectAbout, handedness, multiply, IDENTITY, type Mat } from '../geometry/mat';
import { intersectionOf, unionAll, lastDegraded, resetDegraded, type MultiPoly } from '../geometry/boolean';
import {
  circlePolygon,
  rectPolygon,
  trianglePolygon,
  wedgePolygon,
  dedupe,
} from '../geometry/polygon';
import { PRINTABLE, toRad } from '../units';

/** 覆盖整张纸的楔形半径（mm）。 */
const WEDGE_RADIUS_MM = 2000;

export interface PaperGeometry {
  /** 纸张外轮廓。 */
  paper: Polygon;
  /** 折后可编辑区域 = 楔 ∩ 纸张。 */
  editable: Polygon;
  /** 各层变换矩阵，长度 = layers。 */
  transforms: Mat[];
  /** 各层手性，长度 = layers。 */
  handedness: number[];
  /** 折线角度（度）。 */
  creaseAnglesDeg: number[];
}

/** 正方形纸边长 → 外接圆直径。 */
export const squareSideFromDiameter = (diameterMm: number): number => diameterMm / Math.SQRT2;
export const diameterFromSquareSide = (sideMm: number): number => sideMm * Math.SQRT2;

/** 当前折法在 A4 可打印区内允许的最大成品尺寸。 */
export function maxFinishedSize(fold: FoldDefinition): FinishedSize {
  if (fold.paper === 'square') {
    const side = Math.min(PRINTABLE.widthMm, PRINTABLE.heightMm);
    return { kind: 'diameter', value: [Math.floor(diameterFromSquareSide(side) * 10) / 10] };
  }
  return {
    kind: 'widthHeight',
    value: [PRINTABLE.widthMm, PRINTABLE.heightMm],
  };
}

/** 由成品尺寸得到纸张多边形（居中于原点，毫米）。 */
export function paperPolygon(fold: FoldDefinition, size: FinishedSize): Polygon {
  if (fold.paper === 'square') {
    const d = size.value[0] ?? 260;
    const side = squareSideFromDiameter(d);
    return rectPolygon({ x: 0, y: 0 }, side, side, 0);
  }
  const w = size.value[0] ?? PRINTABLE.widthMm;
  const h = size.value[1] ?? PRINTABLE.heightMm;
  return rectPolygon({ x: 0, y: 0 }, w, h, 0);
}

/** 折线角度（度），长度 = layers，角度按 (baseAngle + k·α) 展开。 */
export function creaseAngles(fold: FoldDefinition): number[] {
  const out: number[] = [];
  for (let k = 0; k < fold.layers; k++) out.push(fold.baseAngleDeg + k * fold.sectorAngleDeg);
  return out;
}

/** 纸张 + 可编辑区域 + 各层变换。 */
export function paperGeometry(foldId: FoldId, size: FinishedSize): PaperGeometry {
  const fold = getFold(foldId);
  const paper = paperPolygon(fold, size);
  const base = toRad(fold.baseAngleDeg);
  const alpha = toRad(fold.sectorAngleDeg);
  const wedge = wedgePolygon(base, alpha, WEDGE_RADIUS_MM);
  const editableMp = intersectionOf(wedge, paper);
  const editable = editableMp[0]?.[0] ?? [];
  const editableOuter = editable.length >= 3 ? editable : paper;

  // T_0 = I, T_{k+1} = Reflect(θ_{k+1}) ∘ T_k；全部为过原点线性变换，行列式交替 ±1。
  const transforms: Mat[] = [];
  let cur: Mat = IDENTITY;
  transforms.push(cur);
  for (let k = 0; k < fold.layers - 1; k++) {
    const m = reflectAbout(base + (k + 1) * alpha);
    cur = multiply(m, cur);
    transforms.push(cur);
  }

  return {
    paper,
    editable: editableOuter,
    transforms,
    handedness: transforms.map(handedness),
    creaseAnglesDeg: creaseAngles(fold),
  };
}

/** 剪口 → 多边形（毫米）。 */
export function cutToPolygon(cut: CutShape): Polygon {
  switch (cut.kind) {
    case 'polygon':
    case 'freehand':
      return dedupe(cut.points, 0.05);
    case 'circle':
      return circlePolygon(cut.center, cut.radius, 64);
    case 'triangle':
      return trianglePolygon(cut.center, cut.radius, cut.rotation);
    case 'rect':
      return rectPolygon(cut.center, cut.width, cut.height, cut.rotation);
  }
}

/** 把 MultiPoly 的每个环应用同一变换。 */
function transformMulti(mp: MultiPoly, m: Mat): MultiPoly {
  return mp.map((rings) => rings.map((ring) => ring.map((p) => apply(m, p))));
}

export interface UnfoldResult {
  /** 展开后的剪掉区域（已裁到纸张范围），用 evenodd 规则渲染。 */
  holes: MultiPoly;
  /** 折后编辑区内的剪口（已裁剪），用于折叠态显示。 */
  foldedCuts: MultiPoly;
  /** 各层副本数量，用于测试断言。 */
  layerCount: number;
  /** 布尔运算是否发生降级（几何可能不完整），用于界面提示"近似结果"。 */
  degraded: boolean;
}

/** 完整几何流水线：剪口 → 裁切 → 合并 → 逐层反射 → 裁剪到纸。 */
export function computeUnfold(
  foldId: FoldId,
  size: FinishedSize,
  cuts: CutShape[],
): UnfoldResult {
  const fold = getFold(foldId);
  const geo = paperGeometry(foldId, size);
  const paperMp: MultiPoly = [[geo.paper]];
  const editableMp: MultiPoly = [[geo.editable]];

  resetDegraded();

  // 1. 剪口 → 多边形
  const cutPolys = cuts.map(cutToPolygon).filter((p) => p.length >= 3);

  // 2. 裁切到折后可编辑区域
  const clipped = cutPolys.map((p) => intersectionOf(p, editableMp)).filter((mp) => mp.length > 0);

  // 3. 合并重叠剪口（并集后再展开，等价且更快）
  const merged: MultiPoly = clipped.length ? unionAll(clipped) : [];

  if (!merged.length) {
    return { holes: [], foldedCuts: [], layerCount: fold.layers, degraded: lastDegraded };
  }

  // 4. 逐层反射展开
  const copies: MultiPoly[] = geo.transforms.map((m) => transformMulti(merged, m));

  // 5. 每层裁到纸张（非自对称纸张上副本会越界），再合并
  const clippedCopies = copies.map((c) => intersectionOf(c, paperMp)).filter((mp) => mp.length > 0);
  const holes = clippedCopies.length > 1 ? unionAll(clippedCopies) : (clippedCopies[0] ?? []);

  return {
    holes,
    foldedCuts: merged,
    layerCount: fold.layers,
    degraded: lastDegraded,
  };
}

/** 折叠态视图数据：可编辑区域 + 已裁剪剪口 + 折线。 */
export function foldedView(foldId: FoldId, size: FinishedSize, cuts: CutShape[]) {
  const geo = paperGeometry(foldId, size);
  const editableMp: MultiPoly = [[geo.editable]];
  const cutPolys = cuts.map(cutToPolygon).filter((p) => p.length >= 3);
  const clipped = cutPolys.map((p) => intersectionOf(p, editableMp)).filter((mp) => mp.length > 0);
  const merged = clipped.length ? unionAll(clipped) : [];
  return {
    editable: geo.editable,
    cutRegion: merged,
    creaseAnglesDeg: geo.creaseAnglesDeg,
  };
}

/** 层数 × 扇区角的校验（测试用）。 */
export function sectorSanity(fold: FoldDefinition): boolean {
  return Math.abs(fold.layers * fold.sectorAngleDeg - 360) < 1e-9 && fold.creaseCount * 2 === fold.layers;
}

/** 展开后的外接半径（mm），用于尺寸语义核对。 */
export function outerRadius(poly: Vec[]): number {
  let r = 0;
  for (const p of poly) r = Math.max(r, Math.hypot(p.x, p.y));
  return r;
}

/** 从中心沿 angleRad 方向到纸张边界的距离。 */
export function maxRadiusAt(fold: FoldDefinition, size: FinishedSize, angleRad: number): number {
  const paper = paperPolygon(fold, size);
  let best = Infinity;
  const dx = Math.cos(angleRad);
  const dy = Math.sin(angleRad);
  for (let i = 0; i < paper.length; i++) {
    const a = paper[i];
    const b = paper[(i + 1) % paper.length];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const denom = dx * ey - dy * ex;
    if (Math.abs(denom) < 1e-12) continue;
    const t = (a.x * ey - a.y * ex) / denom;
    if (t <= 1e-9) continue;
    const px = dx * t;
    const py = dy * t;
    // 检查交点在线段上
    const len2 = ex * ex + ey * ey;
    const s = len2 === 0 ? 0 : ((px - a.x) * ex + (py - a.y) * ey) / len2;
    if (s >= -1e-9 && s <= 1 + 1e-9) best = Math.min(best, t);
  }
  return Number.isFinite(best) ? best : 0;
}

/**
 * 扇区归一化坐标 → 世界坐标。
 * u ∈ [0,1] 沿扇区角度，t ∈ [0,1] 沿该方向的可用半径。
 * 预设与自由绘制都可以用这套坐标描述，保证七种折法共用一套内容管线。
 */
export function sectorPoint(
  foldId: FoldId,
  size: FinishedSize,
  u: number,
  t: number,
  inset = 0.985,
): Vec {
  const fold = getFold(foldId);
  const angle = toRad(fold.baseAngleDeg + u * fold.sectorAngleDeg);
  const r = maxRadiusAt(fold, size, angle) * t * inset;
  return { x: Math.cos(angle) * r, y: Math.sin(angle) * r };
}

/** 生成 (u,t) → 世界坐标 的映射函数，便于预设批量构造。 */
export function sectorMapper(foldId: FoldId, size: FinishedSize) {
  return (u: number, t: number): Vec => sectorPoint(foldId, size, u, t);
}
